import { ErrorHandler, inject, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthErrorCode } from 'utils/enums/auth-error.util';
import { ErrorNotificationService } from './error-notification.service';
import { AuthSessionService } from './auth-session.service';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly injector = inject(Injector);

  handleError(error: HttpErrorResponse | Error): void {
    const errorNotificationService = this.injector.get(
      ErrorNotificationService
    );

    let message = 'An unexpected error occurred.';
    // Server error
    if (error instanceof HttpErrorResponse) {
      // Account deactivated mid-session: every API call is now rejected with
      // this exact code, so a generic "Forbidden" toast would just repeat
      // forever. Say what happened and end the session (logout is designed to
      // work with a broken session). Not a transient failure — the existing
      // "no auto-logout on transient errors" rule doesn't apply.
      if (
        error.status === 403 &&
        (error.error as { message?: string } | null)?.message ===
          AuthErrorCode.ACCOUNT_INACTIVE
      ) {
        errorNotificationService.notifyError(
          'Your account has been deactivated. Contact your administrator.'
        );
        this.injector.get(AuthSessionService).logout();
        return;
      }
      // Handle different status codes
      switch (error.status) {
        case 400:
          message = 'Bad Request. Please check your input.';
          break;
        case 401:
          message = 'Unauthorized. Please sign in.';
          break;
        case 403:
          message =
            'Forbidden. You do not have permission to perform this action.';
          break;
        case 404:
          message =
            'Not Found. The resource you are looking for does not exist.';
          break;
        case 500:
          message = 'Internal Server Error. Please try again later.';
          break;
        default:
          message = `Server error: ${error.message}`;
          break;
      }
    } else if (error instanceof Error) {
      // Client Error
      console.log('Client Error: ', error);
      message = error.message;
    }

    // Notify the error
    errorNotificationService.notifyError(message);
  }
}
